import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { MAX_SIZE_MB } from "../../helper";
import { getKeyOfficeBySlug } from "../../helper/keyOffices";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    maxHeight: "90vh",
    overflow: "auto",
  },
};

const fieldStyle = {
  width: "100%",
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontWeight: "bold",
  marginBottom: "5px",
};

const emptyForm = {
  title: "",
  description: "",
  date: "",
  img: "",
};

function matchesOfficeAnnouncement(update, officeId, officeName) {
  const type = (update.type || "").toLowerCase().trim();
  if (type !== "announcement") return false;

  const category = (update.category || "").toLowerCase().trim();
  const tag = (update.tag || "").toLowerCase().trim();
  const officeIdLower = officeId.toLowerCase();
  const officeNameLower = (officeName || "").toLowerCase().trim();

  const isNavttc =
    officeIdLower.includes("navttc") ||
    officeNameLower.includes("navttc") ||
    officeNameLower.includes("nacttc");

  if (isNavttc) {
    return (
      category === officeIdLower ||
      category.includes("navttc") ||
      category.includes("nacttc") ||
      tag.includes("navttc") ||
      tag.includes("nacttc") ||
      category === officeNameLower ||
      tag === officeNameLower
    );
  }

  return (
    category === officeIdLower ||
    category === officeNameLower ||
    tag === officeIdLower ||
    tag === officeNameLower
  );
}

const OfficeAnnouncements = () => {
  const { officeSlug } = useParams();
  const office = getKeyOfficeBySlug(officeSlug);

  const [items, setItems] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [titleError, setTitleError] = useState("");

  useEffect(() => {
    if (office) fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [office?.id]);

  useEffect(() => {
    if (formData.img && !imgFile) {
      setImgPreview(formData.img);
    }
  }, [formData.img, imgFile]);

  const fetchAnnouncements = async () => {
    setFetchLoading(true);
    try {
      const response = await axiosInstance.get("updates/all");
      const all = response.data.data || [];
      const filtered = all
        .filter((item) => matchesOfficeAnnouncement(item, office.id, office.name))
        .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
      setItems(filtered);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
    setFetchLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "title") {
      const hasSpecialCharacters = /[^a-zA-Z0-9 &,'\-.:()]/.test(value);
      setTitleError(
        hasSpecialCharacters
          ? "Some special characters are not allowed in the title."
          : ""
      );
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > MAX_SIZE_MB) {
      alert(`File size exceeds ${MAX_SIZE_MB} MB. Please upload a smaller file.`);
      e.target.value = "";
      return;
    }
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  };

  const buildPayload = () => {
    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description || "");
    formDataToSend.append("type", "announcement");
    formDataToSend.append("category", office.id);
    formDataToSend.append("tag", office.name);
    formDataToSend.append("date", formData.date);
    formDataToSend.append("time", "");
    formDataToSend.append("venue", "");
    formDataToSend.append("mapLink", "");
    formDataToSend.append("registerURI", "");
    formDataToSend.append("eventURI", "");
    formDataToSend.append("public", "false");
    if (imgFile) {
      formDataToSend.append("img", imgFile);
    }
    return formDataToSend;
  };

  const closeCreate = () => {
    setShowCreateModal(false);
    setFormData(emptyForm);
    setImgFile(null);
    setImgPreview(null);
    setTitleError("");
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditId(null);
    setFormData(emptyForm);
    setImgFile(null);
    setImgPreview(null);
    setTitleError("");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (titleError) {
      alert("Please fix the errors before submitting the form.");
      return;
    }
    setButtonLoading(true);
    try {
      await axiosInstance.post("updates/create", buildPayload());
      await fetchAnnouncements();
      closeCreate();
      Swal.fire("Success!", "Announcement created successfully!", "success");
    } catch (error) {
      console.error("Error creating announcement:", error);
      const message =
        error.response?.data?.message || "Failed to create announcement";
      Swal.fire("Error", message, "error");
    }
    setButtonLoading(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (titleError) {
      alert("Please fix the errors before submitting the form.");
      return;
    }
    setButtonLoading(true);
    try {
      const payload = buildPayload();
      payload.append("_method", "PUT");
      await axiosInstance.post(`updates/change/${editId}`, payload);
      await fetchAnnouncements();
      closeEdit();
      Swal.fire("Success!", "Announcement updated successfully!", "success");
    } catch (error) {
      console.error("Error updating announcement:", error);
      const message =
        error.response?.data?.message || "Failed to update announcement";
      Swal.fire("Error", message, "error");
    }
    setButtonLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;
      await axiosInstance.delete(`updates/delete/${id}`);
      await fetchAnnouncements();
      Swal.fire("Deleted!", "Announcement deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting announcement:", error);
      Swal.fire("Error", "Failed to delete announcement", "error");
    }
  };

  const handleView = async (id) => {
    try {
      setShowEditModal(true);
      setEditId(id);
      setImgFile(null);
      const response = await axiosInstance.get(`updates/single/${id}`);
      const data = response?.data?.data;
      setFormData({
        title: data.title || "",
        description: data.description || "",
        date: data.date || "",
        img: data.img || "",
      });
      setImgPreview(data.img || null);
    } catch (error) {
      console.error("Error fetching announcement:", error);
      Swal.fire({
        icon: "error",
        title: "Error fetching announcement!",
        text: "Please try again.",
        timer: 1500,
      });
    }
  };

  const formFields = useMemo(
    () => (
      <>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            style={fieldStyle}
          />
          {titleError && (
            <span style={{ color: "red", fontSize: 12 }}>{titleError}</span>
          )}
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            style={{ ...fieldStyle, height: 100 }}
          />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Date</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
            style={fieldStyle}
          />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Upload Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={fieldStyle}
          />
        </div>
        {imgPreview && (
          <img
            src={imgPreview}
            alt="Preview"
            style={{ width: "40%", height: "auto", marginBottom: 15 }}
          />
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, titleError, imgPreview]
  );

  if (!office) {
    return <Navigate to="/announcements/registrar" replace />;
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>{office.label}</h2>
          <p style={{ margin: "6px 0 0", color: "#666", fontSize: 14 }}>
            Announcements posted here appear on this office&apos;s public
            announcements page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setFormData(emptyForm);
            setImgFile(null);
            setImgPreview(null);
            setTitleError("");
            setShowCreateModal(true);
          }}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: 5,
            cursor: "pointer",
          }}
        >
          Add Announcement
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
        {fetchLoading ? (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </div>
        ) : items.length === 0 ? (
          <p style={{ color: "#666" }}>No announcements for this office yet.</p>
        ) : (
          items.map((item) => (
            <div
              key={item._id}
              style={{
                width: 250,
                padding: 15,
                borderRadius: 8,
                backgroundColor: "#fff",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                textAlign: "center",
              }}
            >
              <img
                src={item.img || "/uo-16-9.webp"}
                alt={item.title}
                style={{
                  width: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                  marginBottom: 15,
                  aspectRatio: "16/9",
                }}
              />
              <h3 style={{ fontSize: 16 }}>{item.title}</h3>
              {item.date && (
                <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
                  {item.date}
                </p>
              )}
              <Actions
                courseId={item._id}
                onEdit={handleView}
                onDelete={handleDelete}
              />
            </div>
          ))
        )}
      </div>

      <Modal
        isOpen={showCreateModal}
        onRequestClose={closeCreate}
        style={customModalStyles}
        contentLabel="Create Announcement"
      >
        <h2>New announcement — {office.label}</h2>
        <form onSubmit={handleCreate}>
          {formFields}
          <button
            type="submit"
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
              marginTop: 10,
            }}
          >
            {buttonLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Create"
            )}
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={showEditModal}
        onRequestClose={closeEdit}
        style={customModalStyles}
        contentLabel="Edit Announcement"
      >
        <h2>Edit announcement — {office.label}</h2>
        <form onSubmit={handleEdit}>
          {formFields}
          <button
            type="submit"
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
              marginTop: 10,
            }}
          >
            {buttonLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Update"
            )}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default OfficeAnnouncements;
